import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { Backend, EvoBffBackend, EvoOidcBackend } from './types';

export function getBackend(name?: string) {
  if (!window.AccessEvo?.mfeHost) {
    throw new Error('AccessEvo mfeHost is not initialized');
  }
  if (!name || name === 'evo-oidc') {
    return window.AccessEvo.mfeHost.getBackend('evo-oidc') as EvoOidcBackend;
  }
  if (name === 'evo-bff') {
    return window.AccessEvo.mfeHost.getBackend('evo-bff') as EvoBffBackend;
  }
  return window.AccessEvo.mfeHost.getBackend(name) as Backend;
}

export const BackendContext = createContext({ name: '', baseUrl: '' });

export function BackendProvider({
  name = 'evo-oidc',
  baseUrl = '',
  children,
}: {
  name?: string;
  baseUrl?: string;
  children: ReactNode;
}) {
  return <BackendContext.Provider value={{ name, baseUrl }}>{children}</BackendContext.Provider>;
}

export function useBackend() {
  const backendContext = useContext(BackendContext);
  if (!backendContext) {
    throw new Error('useBackend must be used within an BackendProvider');
  }
  const { name, baseUrl } = backendContext;
  const memoBackend = useMemo(() => getBackend(name), [name]);
  const backendApi = useMemo(
    () => ({
      ...memoBackend,
      getBaseUrl: baseUrl ? async () => Promise.resolve(baseUrl) : memoBackend.getBaseUrl,
    }),
    [memoBackend, baseUrl],
  );

  return backendApi;
}
